/**
 * O balcão fecha 01h30. Se a numeração do pedido seguisse a data civil, o pedido
 * de 00h10 de sábado abriria um novo dia no meio do turno de sexta — e o
 * relatório do dia sairia partido em dois.
 *
 * Dia operacional: antes do horário de corte (04h local), a data recua um dia.
 */
const CORTE_HORA_PADRAO = 4;
function partesNoFuso(instante, timezone) {
    const fmt = new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
    const p = {};
    for (const parte of fmt.formatToParts(instante)) {
        if (parte.type !== 'literal')
            p[parte.type] = parte.value;
    }
    return {
        ano: Number(p.year),
        mes: Number(p.month),
        dia: Number(p.day),
        // 'en-CA' com hour12:false entrega 24 para a meia-noite em algumas versões do ICU.
        hora: Number(p.hour) % 24,
        minuto: Number(p.minute),
    };
}
/** Retorna 'YYYY-MM-DD' — a coluna Pedido.dataOperacional é @db.Date. */
export function dataOperacional(instante, timezone = 'America/Sao_Paulo', corteHora = CORTE_HORA_PADRAO) {
    const { ano, mes, dia, hora } = partesNoFuso(instante, timezone);
    const base = Date.UTC(ano, mes - 1, dia);
    const ajustado = new Date(hora < corteHora ? base - 86_400_000 : base);
    return ajustado.toISOString().slice(0, 10);
}
/** Minutos desde 00:00 no fuso da loja. Compara com HorarioFuncionamento. */
export function minutosDoDia(instante, timezone = 'America/Sao_Paulo') {
    const { hora, minuto } = partesNoFuso(instante, timezone);
    return hora * 60 + minuto;
}
/**
 * Hora (0–23) e dia da semana (0 = domingo) no fuso da loja.
 *
 * O dashboard mostra "quando a loja vende", e isso só faz sentido na hora
 * local: `criadoEm` é UTC, então um pedido das 21h de Caieiras aparece como
 * meia-noite se alguém extrair a hora do instante cru. Existe aqui, e não no
 * módulo de relatório, para reusar o mesmo `Intl` do dia operacional — duas
 * leituras de fuso no projeto é uma a mais do que o necessário para divergir.
 */
export function horaEDiaSemana(instante, timezone = 'America/Sao_Paulo') {
    return {
        hora: partesNoFuso(instante, timezone).hora,
        diaSemana: diaSemanaNoFuso(instante, timezone),
    };
}
/**
 * A data 'YYYY-MM-DD' de uma coluna `@db.Date`.
 *
 * O Prisma devolve `@db.Date` como um Date em 00:00 UTC. Passar isso por
 * `dataOperacional()` devolve o DIA ANTERIOR — 00:00Z é 21h do dia de antes em
 * São Paulo, e 21h está depois do corte das 04h, então sai a data civil local.
 * Existe para ninguém alcançar a função errada: a coluna já guarda o dia
 * operacional, e o que falta é só lê-la como texto.
 */
export function diaDaColuna(coluna) {
    return coluna.toISOString().slice(0, 10);
}
/** Dia da semana (0 = domingo) de uma data 'YYYY-MM-DD'. Sem fuso: é uma data. */
export function diaSemanaDe(dia) {
    const [ano, mes, d] = dia.split('-').map(Number);
    return new Date(Date.UTC(ano, mes - 1, d)).getUTCDay();
}
/**
 * A data existe de verdade?
 *
 * `/^\d{4}-\d{2}-\d{2}$/` aceita '2026-02-30', e uma data impossível não
 * explode: `Date.UTC(2026, 1, 30)` devolve 2 de março. O estrago é silencioso —
 * a série diária começa na string literal, pula o dia real e deixa de somar o
 * faturamento daquele dia, enquanto o resumo conta certo. Dois números
 * diferentes na mesma tela, sem erro nenhum.
 */
export function diaValido(dia) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dia))
        return false;
    const [ano, mes, d] = dia.split('-').map(Number);
    if (mes < 1 || mes > 12 || d < 1)
        return false;
    const quando = new Date(Date.UTC(ano, mes - 1, d));
    // Normalizou para outro dia? Então a data não existia.
    return quando.getUTCFullYear() === ano && quando.getUTCMonth() === mes - 1 && quando.getUTCDate() === d;
}
/** Soma dias a uma data 'YYYY-MM-DD', sem passar por fuso nenhum. */
export function somarDias(dia, dias) {
    const [ano, mes, d] = dia.split('-').map(Number);
    return new Date(Date.UTC(ano, mes - 1, d) + dias * 86_400_000).toISOString().slice(0, 10);
}
/** Dias inteiros entre duas datas 'YYYY-MM-DD' (de inclusive, ate inclusive). */
export function diasEntre(de, ate) {
    const ms = (s) => {
        const [a, m, d] = s.split('-').map(Number);
        return Date.UTC(a, m - 1, d);
    };
    return Math.round((ms(ate) - ms(de)) / 86_400_000) + 1;
}
function diaSemanaNoFuso(instante, timezone) {
    const nome = new Intl.DateTimeFormat('en-US', { timeZone: timezone, weekday: 'short' }).format(instante);
    return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(nome);
}
/**
 * Está dentro do horário? Trata turno que vira a madrugada checando também o
 * horário do dia anterior esticado além da meia-noite.
 */
export function dentroDoHorario(instante, horarios, timezone = 'America/Sao_Paulo') {
    const agora = minutosDoDia(instante, timezone);
    const hoje = diaSemanaNoFuso(instante, timezone);
    const ontem = (hoje + 6) % 7;
    const doDia = horarios.find((h) => h.ativo && h.diaSemana === hoje);
    if (doDia && agora >= doDia.abreMin && agora < Math.min(doDia.fechaMin, 1440))
        return true;
    const daVespera = horarios.find((h) => h.ativo && h.diaSemana === ontem && h.fechaMin > 1440);
    if (daVespera && agora < daVespera.fechaMin - 1440)
        return true;
    return false;
}
//# sourceMappingURL=diaOperacional.js.map